<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch} from "vue";
import {useIntersectionObserver, useStorage, watchDebounced} from "@vueuse/core";
import {useRoute, useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import {Avatar, Button, Dialog, Drawer, InputText, Menu, Message, Icon} from "@/Components/Library";
import {ContactDrawer, PageLoading, PageTitle} from "@/Components/Global";
import InviteContactToGroupsDialog from "./InviteContactToGroupsDialog.vue";
import AddContactDrawer from "./AddContactDrawer.vue";
import Str from "@/Utils/Str";
import {deleteContact, getContacts as getContactsFromApi} from "@/Services/contacts";
import {
    getInvitations,
    getOutgoingInvitations,
    resendInvitation,
    respondToInvitation,
    withdrawInvitation,
} from "@/Services/invitations";
import AlphabetList from "@/Components/Global/AlphabetList.vue";
import ContactItem from "./ContactItem.vue";
import useEcho from "@/Composables/echo";
import ConversationFormDialog from "@/Pages/Conversations/ConversationFormDialog.vue";
import PageContainer from "@/Components/Global/PageContainer.vue";
import ContactsIndexEvents from "./contacts-index-events.ts";
import {useMainStore} from "@/Stores/store";
import ContactsIndexFilters from "./ContactsIndexFilters.vue";

const currentRoute = useRoute();
const router = useRouter();
const confirm = useConfirm();
const toast = useToast();
const $store = useMainStore();
const getting = ref(false);
const state = reactive({
    error: null,
    show: {
        contact: false,
        contactDrawer: false,
        filters: false,
        groupDialog: false,
        addContactDialog: !!currentRoute.query.add,
        contactMenu: false,
        invitation: false,
        conversationForm: false,
    },
    type: "contacts" as "contacts" | "invitations",
});

const params = reactive({
    page: 0,
    filter: {
        search: null,
        invitation_status: [],
    },
});

const filters = reactive({
    invitation_status: {
        accepted: false,
        pending: false,
        declined: false,
    },
});

const search = ref(currentRoute.query?.search ?? "");
const contacts = ref([]);
const invitations = ref([]);
const showInvitations = useStorage("show.invitations.contacts", true);
const selectedContact = computed(
    () =>
        contacts.value.find((contact) => contact.id === state.show.contact) ??
        invitations.value?.find((invitation) => invitation.user?.id === state.show.contact)?.user,
);
const showContactMenu = computed({
    get: () => !!state.show.contact && !!state.show.contactMenu,
    set: (val) => (state.show.contactMenu = val),
});
const selectedInvitation = computed(() =>
    invitations.value?.find((invitation) => invitation.id === state.show.invitation),
);
const showInvitationDrawer = computed({
    get: () => !!state.show.invitation,
    set: (val) => (state.show.invitation = val),
});

const totalActiveFilters = computed(() => {
    let count = 0;
    if (state.type !== "contacts") {
        count++;
    }

    return count;
});

const getRecords = async () => {
    try {
        getting.value = true;
        let data = [];
        let total = 0;
        if (state.type === "contacts") {
            const resp = await getContactsFromApi(params);
            data = resp.data;
            console.log(resp);
            total = resp.total;
            if (!resp.total && !search.value) {
                showInvitations.value = true;
            }
        } else {
            const resp = await getOutgoingInvitations(params);
            data = resp.data;
            total = resp.total;
            console.log(resp);
        }

        if (params.page > 0) {
            contacts.value = [...contacts.value, ...data];
        } else {
            contacts.value = data;
        }

        loadMore.value = data.length && contacts.value.length < total;

        if (currentRoute.name === "contacts.show") {
            let contact = contacts.value.find((C) => C.contact_id === currentRoute.params.contact);
            if (!contact) {
                contact = invitations.value.find((C) => C.contact_id === currentRoute.params.contact);
            }
            if (contact) {
                state.show.contact = contact.id;
                state.show.contactDrawer = true;
            }
        }

        console.log(contactsByLetter);
    } catch (e) {
        console.error(e);
        state.error = e.response?.data?.message || e.message || "An error occurred";
    } finally {
        getting.value = false;
    }
};
getRecords();
getInvitations({for: "user", status: "pending"}).then(({data}) => (invitations.value = data));

const contactsByLetter = computed(() => {
    const sortedContacts = [...contacts.value].sort((a, b) => {
        const aSort = a.last_name || a.first_name || "";
        const bSort = b.last_name || b.first_name || "";
        return aSort.localeCompare(bSort);
    });

    const grouped = new Map<string, any[]>();
    sortedContacts.forEach((contact) => {
        const letter = (contact.last_name?.[0] ?? contact.first_name?.[0]).toUpperCase();
        if (!grouped.has(letter)) {
            grouped.set(letter, []);
        }
        grouped.get(letter).push(contact);
    });

    return grouped;
});

const menu = ref<Menu>();
const contactMenuItemsBase = {
    groupInvite: {
        show: ["invitation_status", "accepted"],
        label: "Invite to group",
        icon: "prime:plus",
        command: () => {
            state.show.groupDialog = true;
            state.show.contactMenu = false;
        },
    },
    delete: {
        show: ["to", undefined],
        label: "Delete",
        icon: "prime:trash",
        command: async (event) => {
            confirm.require({
                group: undefined,
                target: event.target as HTMLElement,
                header: `Delete
                ${selectedContact.value.name} from contacts`,
                message: `Are you sure you want to delete this contact?`,
                acceptLabel: "Yes",
                rejectLabel: "No",
                acceptClass:
                    "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
                rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
                accept: async () => {
                    console.log(selectedContact.value);
                    await deleteContact(selectedContact.value.contact_id);
                    toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: "Contact has been deleted.",
                        life: 3000,
                    });
                    contacts.value = contacts.value.filter((c) => c.id !== selectedContact.value.id);
                },
                reject: () => {
                    return;
                },
            });
            state.show.contactMenu = false;
        },
        class: "text-red-600 hover:bg-red-100",
    },
    resendInvitation: {
        show: ["to", "user"],
        label: "Resend invitation",
        icon: "prime:envelope",
        command: async () => {
            confirm.require({
                header: `Resend invitation`,
                message: `Are you sure you want to resend this invitation?`,
                acceptLabel: "Yes",
                rejectLabel: "No",
                acceptClass:
                    "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
                rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
                accept: async () => {
                    await resendInvitation(selectedContact.value.id);
                    toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: "Invitation has been resent.",
                        life: 3000,
                    });
                },
            });
            state.show.contactMenu = false;
        },
    },
    withdrawInvitation: {
        show: ["to", "user"],
        label: "Withdraw invitation",
        icon: "prime:trash",
        command: async () => {
            confirm.require({
                header: `Withdraw invitation`,
                message: `Are you sure you want to withdraw this invitation?`,
                acceptLabel: "Yes",
                rejectLabel: "No",
                acceptClass:
                    "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
                rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
                accept: async () => {
                    await withdrawInvitation(selectedContact.value.id);
                    toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: "Invitation has been withdrawn.",
                        life: 3000,
                    });
                    contacts.value = contacts.value.filter((c) => c.id !== selectedContact.value.id);
                },
            });
            state.show.contactMenu = false;
        },
        class: "text-red-600 hover:bg-red-100",
    },
};
const contactMenuItems = ref([
    {
        label: "",
        items: [],
    },
]);

const toggleContactMenu = (contact) => {
    contactMenuItems.value[0].items = Object.values(contactMenuItemsBase).filter((item) => {
        if (item.show) {
            return contact[item.show[0]] === item.show[1];
        }
        return true;
    });

    state.show.contact = contact.id;
    state.show.contactMenu = contact.id;
    contactMenuItems.value[0].label = selectedContact.value?.name;
};

const confirmRespondToInvitation = async (event: Event, contact: any, response: "accept" | "decline") => {
    console.log(response);
    let buttonClasses = "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold";
    if (response === "decline") {
        buttonClasses += " bg-red-600 focus:ring-red-400";
    } else {
        buttonClasses += " bg-askr-green";
    }
    confirm.require({
        group: undefined,
        target: event.target as HTMLElement,
        header: `${Str.of(response).capitalise().toString()} invitation`,
        message: `Are you sure you want to ${response} this invitation?`,
        acceptLabel: Str.of(response).capitalise().toString(),
        rejectLabel: "No",
        acceptClass: buttonClasses,
        rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
        accept: async () => {
            const data = await respondToInvitation(contact.id, response);
            console.log(data);
            $store.decrementContactInvitations();
            if (response === "accept") {
                toast.add({
                    severity: "success",
                    summary: "Invitation accepted",
                    detail: "User has been added to your contacts.",
                    life: 3000,
                });
            } else {
                toast.add({
                    severity: "success",
                    summary: "Invitation declined",
                    detail: "Invitation has been removed.",
                    life: 3000,
                });
            }
            // getInvitations({for: "user"}).then(({data}) => (invitations.value = data));
            console.log(invitations.value, contact);
            invitations.value = invitations.value.filter((i) => i.id !== contact.id);
            await getRecords();
        },
        reject: () => {
            return;
        },
    });
};

const showContact = async (contact) => {
    try {
        if (contact.contact_id) {
            //TODO groups you have in common
            // const {data} = await getContact(contact.contact_id);
            // console.log(data);
        }
        state.show.contact = contact.id;
        state.show.contactDrawer = true;
    } catch (e) {
        console.log(e);
    }
};

const loadMore = ref(false);
const loadMoreIntersection = useTemplateRef("loadMoreIntersection");
const {stop} = useIntersectionObserver(
    loadMoreIntersection,
    ([{isIntersecting}], observerElement) => isIntersecting && loadMore.value && params.page++,
);

const applyFilters = () => {
    const newParams = {
        page: 0,
        filter: {
            ...params.filter,
            invitation_status: Object.entries(filters.invitation_status)
                .filter(([, value]) => value)
                .map(([key]) => key),
        },
    };
    Object.assign(params, newParams);
    state.show.filters = false;
};

const clearFilters = () => {
    filters.invitation_status = {
        accepted: false,
        pending: false,
        declined: false,
    };
    state.type = "contacts";
    applyFilters();
};

const createConversation = () => {
    state.show.contactDrawer = false;
    state.show.conversationForm = true;
};

const handleContactUpdate = (contact) => {
    if (!contact) {
        return;
    }

    const user = contacts.value.find((c) => c.contact_id === contact.id);
    user.notes = contact.notes;
    user.preferences = contact.preferences;
};

useEcho(ContactsIndexEvents(contacts, state));

watchDebounced(
    search,
    async () => {
        loadMore.value = false;
        const newParams = {
            page: 0,
            filter: {
                ...params.filter,
                search: search.value,
            },
        };
        Object.assign(params, newParams);
    },
    {debounce: 500},
);

watch(() => params, getRecords, {deep: true});
</script>
<template>
    <PageContainer>
        <div class="mb-2">
            <PageTitle :title="`Contacts${state.type === 'invitations' ? ' (pending)' : ''}`" :show-back-button="false">
                <div class="ml-auto">
                    <Button
                        text
                        class="text-xs font-normal"
                        @click="
                            state.type = state.type === 'contacts' ? 'invitations' : 'contacts';
                            applyFilters();
                        "
                    >
                        Show {{ state.type === "contacts" ? "pending" : "contacts" }}
                    </Button>
                </div>
            </PageTitle>
        </div>
        <div class="-sticky top-2 z-10 mb-3">
            <InputText type="search" v-model="search" placeholder="Search contacts" class="w-full" />
        </div>
        <div v-if="invitations?.length" class="my-3" v-auto-animate>
            <div v-if="showInvitations" class="flex flex-col gap-2">
                <template v-for="invitation in invitations" :key="invitation.id">
                    <Message>
                        <template #container>
                            <div class="w-full flex-wrap items-center justify-between px-6 py-3 text-sm md:flex">
                                <div class="mb-2 text-center md:mb-0">
                                    <span
                                        class="cursor-pointer font-bold"
                                        @click="showInvitationDrawer = invitation.id"
                                    >
                                        {{ invitation.user.name }}
                                    </span>
                                    invites you to connect
                                </div>
                                <div class="flex items-center justify-center gap-3">
                                    <Button
                                        text
                                        class="text-sm !font-normal text-red-600 hover:brightness-110"
                                        @click="confirmRespondToInvitation($event, invitation, 'decline')"
                                    >
                                        Decline
                                    </Button>
                                    <Button
                                        class="border border-askr-green !px-3 !py-1 text-sm text-askr-green hover:brightness-110"
                                        @click="confirmRespondToInvitation($event, invitation, 'accept')"
                                    >
                                        Accept
                                    </Button>
                                </div>
                            </div>
                        </template>
                    </Message>
                </template>
            </div>
            <div v-else>
                <Message @click="showInvitations = !showInvitations" class="cursor-pointer">
                    <template #container>
                        <div class="flex w-full items-center gap-2 px-4 py-3 text-sm">
                            <span
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-askr-green font-bold text-white"
                            >
                                {{ invitations?.length }}
                            </span>
                            <h3 class="font-bold">People have invited you to connect</h3>
                        </div>
                    </template>
                </Message>
            </div>
            <div class="mt-1 flex w-full justify-end">
                <button
                    type="button"
                    class="flex items-center gap-1 text-xs"
                    @click="showInvitations = !showInvitations"
                >
                    <Icon :icon="showInvitations ? 'akar-icons:chevron-up' : 'akar-icons:chevron-down'" />
                    {{ showInvitations ? "Hide" : "Show" }} invitations
                </button>
            </div>
        </div>
        <div class="flex items-stretch gap-6">
            <!-- filters -->
            <div class="hidden h-full w-full max-w-sm flex-none 2xl:block">
                <div class="sticky top-2">
                    <ContactsIndexFilters
                        v-model="state.type"
                        :total-active-filters="totalActiveFilters"
                        @apply="applyFilters"
                        @clear="clearFilters"
                    />
                </div>
            </div>
            <!-- content -->
            <div class="grow divide-y dark:divide-stone-800">
                <template v-for="[letter, contacts] in contactsByLetter" :key="letter">
                    <AlphabetList :letter="letter">
                        <template v-for="contact in contacts" :key="contact.id">
                            <ContactItem
                                :contact="contact"
                                @menu="toggleContactMenu(contact)"
                                @show="showContact(contact)"
                            />
                        </template>
                    </AlphabetList>
                </template>
                <PageLoading v-if="getting" class="pt-16" />
                <p v-if="state.error" class="mt-8 rounded border border-red-600 bg-red-50 p-2 text-center">
                    {{ state.error }}
                </p>
            </div>
        </div>
        <div v-if="!getting && !state.error && loadMore">
            <span ref="loadMoreIntersection"></span>
            <div class="flex justify-center">
                <Button @click="params.page++" :loading="!!getting" :disabled="!!getting || !loadMore">
                    {{ loadMore || !contacts.length ? "Load more" : "No more results" }}
                </Button>
            </div>
        </div>
        <div v-if="!contacts?.length && !getting" class="flex flex-col items-center justify-center gap-2 p-6 sm:h-auto">
            <span>
                {{ search || totalActiveFilters ? "No matching contacts found." : "You have no contacts yet." }}
            </span>
            <Button
                class="bg-askr-green text-white hover:brightness-110"
                :icon="{
                    icon: 'lucide:user-plus',
                    width: '24',
                }"
                @click="state.show.addContactDialog = true"
            >
                Add a new contact
            </Button>
        </div>
        <div class="pointer-events-none fixed bottom-0 right-0 z-10 mx-auto mt-auto flex w-full justify-end px-4 pb-4">
            <div class="pointer-events-auto flex w-full max-w-7xl items-end justify-between">
                <Button
                    class="-shadow rounded-md bg-askr-green px-8 py-2 text-center font-bold text-white shadow-stone-600 2xl:hidden"
                    :icon="{
                        icon: 'hugeicons:filter',
                        width: '24',
                    }"
                    @click="state.show.filters = true"
                >
                    {{ totalActiveFilters ? totalActiveFilters : null }}
                </Button>
                <Button
                    :icon="{icon: 'mdi:plus', width: '36'}"
                    rounded
                    class="-shadow pointer-events-auto ml-auto h-14 w-14 bg-askr-green text-white shadow-stone-600"
                    @click="state.show.addContactDialog = true"
                />
            </div>
        </div>
        <Dialog
            v-model:visible="showContactMenu"
            modal
            dismissable-mask
            class="max-w-md overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <div class="bg-white">
                    <Menu ref="menu" :model="contactMenuItems" />
                </div>
            </template>
        </Dialog>
        <InviteContactToGroupsDialog :current-contact="selectedContact" v-model:visible="state.show.groupDialog" />
        <AddContactDrawer
            v-model:visible="state.show.addContactDialog"
            @invited="
                () => {
                    search = null;
                    getRecords();
                }
            "
        />
        <Drawer
            v-model:visible="state.show.filters"
            position="bottom"
            header="Filters"
            class="max-h-dvh max-w-3xl overflow-y-auto"
        >
            <div class="pb-12">
                <ContactsIndexFilters
                    v-model="state.type"
                    :total-active-filters="totalActiveFilters"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
        </Drawer>
        <Drawer
            v-model:visible="showInvitationDrawer"
            position="bottom"
            header="Invitation"
            class="max-h-dvh max-w-3xl overflow-y-auto"
        >
            <div>
                <Message class="mb-6">
                    <template #container>
                        <div class="w-full flex-wrap items-center justify-between px-6 py-3 text-sm md:flex">
                            <div class="mb-2 text-center md:mb-0">
                                <span class="font-bold">
                                    {{ selectedInvitation.user.name }}
                                </span>
                                invites you to connect
                            </div>
                            <div class="flex items-center justify-center gap-3">
                                <Button
                                    text
                                    class="text-sm !font-normal text-red-600 hover:brightness-110"
                                    @click="confirmRespondToInvitation($event, selectedInvitation, 'decline')"
                                >
                                    Decline
                                </Button>
                                <Button
                                    class="border border-askr-green !px-3 !py-1 text-sm text-askr-green hover:brightness-110"
                                    @click="confirmRespondToInvitation($event, selectedInvitation, 'accept')"
                                >
                                    Accept
                                </Button>
                            </div>
                        </div>
                    </template>
                </Message>
                <div class="mb-12 flex flex-col items-center gap-4">
                    <Avatar
                        :user="selectedInvitation.user"
                        class="h-16 w-16 text-3xl"
                        :class="[`bg-${selectedInvitation.user.profile?.color?.class ?? 'stone-300'}`]"
                        shape="circle"
                    />
                    <h2 class="text-xl font-bold sm:text-3xl">
                        {{ selectedInvitation.user.name }}
                    </h2>
                </div>
            </div>
        </Drawer>
        <ContactDrawer
            v-model="state.show.contactDrawer"
            :user="selectedContact"
            @contact-updated="handleContactUpdate"
            @create-conversation="createConversation"
        />
        <ConversationFormDialog v-model="state.show.conversationForm" :participants="[selectedContact]" />
    </PageContainer>
</template>
<style scoped></style>
